import { useState } from 'react';

const coreServices = [
  {
    title: 'Site Selection & Venue Sourcing',
    icon: 'ri-map-pin-line',
    description: 'Strategic venue identification and contract negotiation aligned with your program objectives and budget.',
    features: [
      'Destination and market evaluation',
      'Custom venue sourcing & shortlist development',
      'Site inspections and comparative analysis',
      'Contract negotiation & value optimization'
    ],
    bgColor: 'bg-[#0B1F3B]',
    textColor: 'text-white',
    accentColor: '#D5BA6B'
  },
  {
    title: 'Full-Service Event Management',
    icon: 'ri-calendar-event-line',
    description: 'Comprehensive program leadership from concept through on-site execution.',
    features: [
      'Program strategy & experience design',
      'Vendor & production co-ordination',
      'Timeline & logistics management',
      'On-site oversight & execution',
      'Budget oversight and cost control'
    ],
    bgColor: 'bg-white',
    textColor: 'text-gray-900',
    accentColor: '#D5BA6B'
  },
  {
    title: 'Incentive Travel Programs',
    icon: 'ri-flight-takeoff-line',
    description: 'Strategically designed group travel experiences that reward performance and strengthen engagement.',
    features: [
      'Destination & resort selection',
      'Accommodation & travel co-ordination',
      'Curated activities & hosted events',
      'Budget planning & cost control'
    ],
    bgColor: 'bg-black',
    textColor: 'text-white',
    accentColor: '#D5BA6B'
  }
];

const additionalSupport = [
  {
    title: 'Meeting & Conference Management',
    icon: 'ri-team-line',
    description: 'Strategic co-ordination of corporate meetings and conferences from planning through on-site execution.',
    features: [
      'Registration & RSVP management',
      'Attendee experience co-ordination',
      'Production & AV management',
      'Catering & venue logistics',
      'Budget oversight & cost control'
    ],
    bgColor: 'bg-[#0B1F3B]',
    textColor: 'text-white',
    accentColor: '#D5BA6B'
  },
  {
    title: 'Curated Entertainment',
    icon: 'ri-music-2-line',
    description: 'Professionally sourced entertainment solutions that enhance corporate events and hosted experiences.',
    features: [
      'Live bands, DJs & professional performers',
      'MCs & program hosts',
      'Interactive & immersive activations',
      'Talent sourcing & contract management'
    ],
    bgColor: 'bg-white',
    textColor: 'text-gray-900',
    accentColor: '#D5BA6B'
  },
  {
    title: 'Tradeshow & Exhibit Management',
    icon: 'ri-layout-grid-line',
    description: 'Professional exhibit management from planning through on-site execution and dismantle.',
    features: [
      'Exhibit planning & vendor management',
      'Production & fabrication co-ordination',
      'Installation & dismantle oversight',
      'On-site exhibit staffing & support co-ordination'
    ],
    bgColor: 'bg-black',
    textColor: 'text-white',
    accentColor: '#D5BA6B'
  },
  {
    title: 'Group Dining & Hosted Experiences',
    icon: 'ri-restaurant-line',
    description: 'Curated offsite dining and hosted experiences in Toronto, designed to complement your corporate program.',
    features: [
      'Private dining venue sourcing',
      'Restaurant buyouts & VIP reservations',
      'Premium suites & ticket co-ordination',
      'Contract negotiation & logistics management'
    ],
    bgColor: 'bg-[#0B1F3B]',
    textColor: 'text-white',
    accentColor: '#D5BA6B'
  },
  {
    title: 'Transportation & Ground Logistics',
    icon: 'ri-bus-line',
    description: 'Structured co-ordination of group transportation to ensure seamless attendee movement.',
    features: [
      'Airport transfers & executive vehicles',
      'Shuttle programs & coach co-ordination',
      'Routing, scheduling & vendor management',
      'On-site transportation oversight'
    ],
    bgColor: 'bg-white',
    textColor: 'text-gray-900',
    accentColor: '#D5BA6B'
  },
  {
    title: 'Corporate & Event Gifting',
    icon: 'ri-gift-line',
    description: 'Curated gifting solutions aligned with your brand, audience and program objectives.',
    features: [
      'Custom gift sourcing & curation',
      'Branded merchandise & packaging',
      'Bulk fulfillment & personalization',
      'Co-ordinated delivery logistics'
    ],
    bgColor: 'bg-black',
    textColor: 'text-white',
    accentColor: '#D5BA6B'
  }
];

export default function Services() {
  const [hoveredIndex, setHoveredIndex] = useState<string | null>(null);

  const renderCard = (service: typeof coreServices[0], key: string) => (
    <div
      key={key}
      className={`${service.bgColor} ${service.textColor} rounded-2xl p-8 sm:p-10 transition-all duration-500 cursor-pointer border border-gray-200/40 ${
        hoveredIndex === key ? 'transform scale-105 shadow-xl' : 'shadow-sm'
      }`}
      onMouseEnter={() => setHoveredIndex(key)}
      onMouseLeave={() => setHoveredIndex(null)}
    >
      <div className="w-12 sm:w-16 h-12 sm:h-16 flex items-center justify-center mb-4 sm:mb-6" style={{ color: service.accentColor }}>
        <i className={`${service.icon} text-4xl sm:text-5xl`}></i>
      </div>

      <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 leading-tight">{service.title}</h3>
      <p className={`text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed ${service.textColor === 'text-white' ? 'text-white/80' : 'text-gray-600'}`}>
        {service.description}
      </p>

      <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
        {service.features.map((feature, idx) => (
          <li key={idx} className="flex items-start">
            <i className="ri-check-line text-lg sm:text-xl mr-2 mt-0.5" style={{ color: service.accentColor }}></i>
            <span className="text-xs sm:text-sm leading-relaxed">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <section id="services" className="py-24 bg-stone-100 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-3 sm:mb-4">Our Services</h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Comprehensive solutions tailored to your program objectives.
          </p>
        </div>

        {/* Core Services */}
        <div className="mb-14">
          <div className="mb-6 sm:mb-8">
            <h4 className="text-[22px] font-bold tracking-[1.5px] uppercase" style={{ color: '#D5BA6B' }}>
              <a href="#services">Core Services</a>
            </h4>
            <div className="w-12 h-[2px] mt-2 rounded-full" style={{ backgroundColor: '#0B1F3B' }} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {coreServices.map((service, index) => renderCard(service, `core-${index}`))}
          </div>
        </div>

        {/* Additional Support */}
        <div>
          <div className="mb-6 sm:mb-8">
            <h4 className="text-[22px] font-bold tracking-[1.5px] uppercase" style={{ color: '#D5BA6B' }}>
              <a href="#services">Additional Support</a>
            </h4>
            <div className="w-12 h-[2px] mt-2 rounded-full" style={{ backgroundColor: '#0B1F3B' }} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {additionalSupport.map((service, index) => renderCard(service, `additional-${index}`))}
          </div>
        </div>

        {/* Closing Statement */}
        <div className="mt-16 sm:mt-20 text-center max-w-4xl mx-auto">
          <div className="w-12 h-[2px] mx-auto mb-6" style={{ background: 'linear-gradient(to right, #D5BA6B, #C4A855)' }} />
          <p className="text-lg sm:text-xl font-semibold text-gray-900 leading-relaxed mb-8">
            From venue sourcing to flawless execution, MGroup Global delivers seamless programs across Canada, the U.S. and worldwide.
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold text-sm px-8 py-3.5 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Request a Consultation
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10">
              <i className="ri-arrow-right-line text-base" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
